import {Component, Inject, OnInit, PLATFORM_ID} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {Store} from '@ngrx/store';
import {
  AlertsCopy,
  Assets,
  Copy,
  MyPlan,
  MyPlansResponse,
  PaymentMethodDetails,
  PlanCard,
  PlanCategory,
  Profile,
  RecommendedOffer,
  RecommendedOffers,
  Vehicle,
  ExpiredPlanPayload,
  UIFeatureFlag,
  UIFeatureFlagsEnum
} from '@ox/typings';
import {AutoDestroy, WINDOW} from '@ox/utils';
import {
  AssetsService,
  EcppDataService,
  MyPlansUnifiedService,
  ShopDataService
} from '@webu/data';
import {selectActiveVehicle, selectProfile,AdobeLaunchService,MyPlansActions, selectFeatureFlag, selectAuthState, FeatureFlagService} from '@ox/data';
import {APP_BASE_HREF, DOCUMENT, isPlatformBrowser} from '@angular/common';
import {
  Observable,
  Subject,
  catchError,
  combineLatest,
  filter,
  forkJoin,
  lastValueFrom,
  map,
  of,
  switchMap,
  take,
  takeUntil,
  takeWhile,
  tap
} from 'rxjs';
import {ExpiredPlanModalComponent} from './expired-plan-modal/expired-plan-modal.component';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {TranslateService} from '@ox/translate';


@Component({
  selector: 'webu-my-plans-page',
  templateUrl: './my-plans.component.html'
})
export class MyPlansPageComponent implements OnInit {
  @AutoDestroy destroy$: Subject<void> = new Subject<void>();

  myPlansLoaded = false;
  recommendedLoaded = false;
  copyLoaded = false;
  showRecommended = false;
  userRole:string;
  userName:string;
  recommendedOffers: RecommendedOffer[];
  myPlans: MyPlansResponse;
  planNames: string[];
  year: string;
  model: string;
  alerts?: AlertsCopy[];
  paymentMethod?: PaymentMethodDetails;
  myPlanLabel :string;
  hasError: boolean;
  errorMessage: string;
  make: string;
  vin: string;
  cancelPlanEnabled = false;
  showCancelMessage = false;
  includeCancelFAQLink = false;
  orderIdMap: { [key: string]: string } = {};
  sharedNavEnabled$ = this.translate.get('isSharedNavEnabled');
  userState$ = this.store.select(selectAuthState);
  
  assets$ = this.assetService.getAssets().pipe(
    takeUntil(this.destroy$),
    filter((assets): assets is Assets => !!assets));


  cancelPlanToggle$ = this.store.select(selectFeatureFlag({id: UIFeatureFlag.CANCELLATION, group: 'UI'}))
    .pipe(
      takeWhile((val) => !val, true),
      tap((flag) => this.cancelPlanEnabled = flag === 'true')
  ).subscribe();

  profile$ = this.store.select(selectProfile).pipe(
    takeUntil(this.destroy$),
    filter((profile): profile is Profile => !!profile));

  activeVehicle$ = this.store.select(selectActiveVehicle).pipe(
    takeUntil(this.destroy$),
    filter((vehicle): vehicle is Vehicle => !!vehicle),
    map((vehicle) => {
      this.refreshStates();
      if (!vehicle?.onStarActive) this.window.location.href = `${this._baseHref}onstar/activate`;
      this.year = vehicle.year;
      this.model = vehicle.model;
      this.userRole = vehicle.roleCode;
      this.userName = vehicle.accountName;
      this.make = vehicle.make;
      this.vin = vehicle.vin;
      return vehicle;
    })
  );

  myPlans$(vin: string, profileID: string, stateProvince?: string): Observable<MyPlansResponse> {
    return this.shopDataService.getMyPlansData(vin,profileID, stateProvince).pipe(
      take(1),
      map((response) => {
        this.myPlansLoaded = true;
        this.hasError = false;
        if (response) {
          this.myPlans = response;
          this.planNames = this.getPlanNamesArray(response.categories);
          this.orderIdMap = this.mapOrderIds(response.categories);
          this.dataLayer.setActiveOnstarProduct(this.planNames);
          this.setPaymentMethod();
          this.store.dispatch(MyPlansActions.update({myCurrentPlans: this.planNames}));
          return response;
        } else {
          this.hasError = true;
          throw new Error('Get My Plans Failed');
        }
      }),
      catchError((err) => {
        console.log(err);
        this.hasError = true;
        throw new Error('Get My Plans Failed');
      })
    );
  }

  copy$(): Observable<Copy> {
    return this.shopDataService.getCopyData().pipe(
      take(1),
      map((copy) => {
        this.copyLoaded = true;
        if(copy){
            this.myPlanLabel = this.shopDataService.getGenericCopy('myplans.myplans')?? '';
            this.errorMessage = this.shopDataService.getGenericCopy('myplans.load.failure')?? '';
            this.setTitle(this.myPlanLabel);
        }
        return copy;
      }),
      catchError(() => {
        this.hasError = true;
        throw new Error('Get Copy Data Failed');
      })
    );
  }

  recommendedOffers$(vehicle: Vehicle): Observable<RecommendedOffers | undefined> {
    return this.myPlansService.getRecommendedOffers(vehicle.vin).pipe(
      takeUntil(this.destroy$),
      take(1),
      map((response) => {
        this.recommendedLoaded = true;
        if (response) {
          this.recommendedOffers = response.recommendedOffers;
          this.showRecommended = (this.recommendedOffers.length > 0 && vehicle.roleCode === 'OWNER');
          return response;
        } else{
          return undefined;
        }
      }),
      catchError(() => {
        this.recommendedLoaded = true;
        return of(undefined);
      })
    );
  }

  private ftcFlag$ = this.featureFlagService.featureFlags$
    .pipe(
      take(1),
      map(ff => ff.UI[UIFeatureFlagsEnum.OC_FTC_ENABLED]));

  ftc$ = (vehicle: Vehicle) => this.ftcFlag$.pipe(
    take(1),
    switchMap((ftcFlag) => {
      return (vehicle && vehicle.vin && ftcFlag) ? this.ecppDataService.getFTCAcceptanceData() : of(null);
    })
  );

  constructor (
    private store: Store,
    private myPlansService: MyPlansUnifiedService,
    private shopDataService: ShopDataService,
    private assetService: AssetsService,
    private titleService: Title,
    @Inject(WINDOW) private window: Window,
    @Inject(APP_BASE_HREF) private _baseHref: string,
    private modalService: BsModalService,
    public modalRef: BsModalRef,
    private dataLayer: AdobeLaunchService,
    private translate: TranslateService,
    @Inject(PLATFORM_ID) private platformId: any,
    @Inject(DOCUMENT) private document: Document,
    private featureFlagService: FeatureFlagService,
    private ecppDataService: EcppDataService
  ) {
    if (isPlatformBrowser(this.platformId)) {
      const script = this.document.createElement('script');
      script.setAttribute('id', 'shop-nav');
      script.setAttribute('type', 'module');
      script.src = '/ecom/nav/build/gm-shared-nav.esm.js';
      this.document.head.prepend(script);
    }
  }

  ngOnInit(): void {
    combineLatest([
      this.activeVehicle$,
      this.profile$,
      this.assets$
    ]).pipe(
      takeUntil(this.destroy$),
      switchMap(([vehicle, profile]) => forkJoin([
        this.myPlans$(vehicle.vin, profile.globalProfileId, profile.stateProvince),
        this.recommendedOffers$(vehicle),
        this.copy$(),
        this.ftc$(vehicle)
      ]))
    ).subscribe({
      next:()=>{
        if(this.copyLoaded && this.myPlans.alertIds) {
          this.alerts = this.shopDataService.getAlertsByAlertIds(this.myPlans.alertIds);
        }
        this.dataLayer.setMyPlansProducts(this.myPlans, this.recommendedOffers);
        this.renderExpiredPlansModal();
        // check whether to show cancel message or not
        this.resolveCancelMessage();
      },
      error: (err) => {
        this.hasError = true;
        console.error(err);
      }
    });
  }

  refreshStates() {
    this.myPlansLoaded = false;
    this.recommendedLoaded = false;
    this.copyLoaded = false;
    this.showRecommended = false;
  }

  setPaymentMethod(){
    this.myPlans.categories?.find(
      (category)=>category.planCard.find((card:PlanCard)=>{
        if(card.paymentMethod){
          this.paymentMethod = card.paymentMethod as PaymentMethodDetails | undefined;
        }
      }
    ));
  }

  public setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }

  renderExpiredPlansModal() {
    if(this.myPlans?.expiredPlanModal?.plansAndOrdersIds) {
      this.profile$.pipe(
        switchMap((profile) =>
          this.shopDataService.getExpiryStatusPresented(
            this.vin,
            profile.onStarAcctId,
            this.reformatExpiredPlanResponse(this.myPlans?.expiredPlanModal?.plansAndOrdersIds))
      )).subscribe();
      this.modalService.show(ExpiredPlanModalComponent, {
        initialState: {expiredPlans: this.myPlans?.expiredPlanModal?.plansAndOrdersIds}
      });
    }
  }

  reformatExpiredPlanResponse(expiredPlans?: MyPlan[]) {
    // expiryStatusPresented relies on old my plans response
    const reformattedPlans: ExpiredPlanPayload[] = [];
    if(expiredPlans !== undefined) {
      for(let i = 0; i < expiredPlans.length; i++) {
        reformattedPlans.push(
          {
            planId: expiredPlans[i].planName,
            planCode: expiredPlans[i].planCode,
            OrderId: expiredPlans[i].orderId
          });
      }
    }
    return reformattedPlans;
  }

  getPlanNamesArray(categories: PlanCategory[]){
    const products:string[]=[];
    const subscriptions = categories.filter((category)=>category.type === 'SUBSCRIPTIONS');
    subscriptions.forEach((category)=>{
      category.planCard.forEach((card)=>{
        card.plans.forEach((plan)=>{
          products.push(plan.planName);
        });
      });
    });
    return products;
  }

  checkCancelButtons(categories: PlanCategory[]) {
    let showCancelText = true;
    const subscriptions = categories.filter((category)=>category.type === 'SUBSCRIPTIONS');
    subscriptions.forEach((category)=>{
      category.planCard.forEach((card)=>{
        if(card.buttons){
          card.buttons.forEach((button)=>{
            if(button.action[0] === 'CANCEL'){
              showCancelText = false;
            }
          });
        }
      });
      });
      return showCancelText;
    }

    mapOrderIds(categories: PlanCategory[]) {
      const tempMap : {[key: string]: string} = {};
      const subscriptions = categories.filter((category)=>category.type === 'SUBSCRIPTIONS');
      subscriptions.forEach((category)=>{
        category.planCard.forEach((card)=>{
            card.plans.forEach((plan)=>{
              if(plan.planCode && plan.orderId){
                tempMap[plan.planCode] = plan.orderId;
              }
            });
        });
        });
      return tempMap;
      }

  /**
   * This function determines whether to show or not to show the cancel message on my plans page.
   */
  async resolveCancelMessage() {
    // show message and faq link as default
    // for cases: based on existing logic or Canadian residents
    this.showCancelMessage = true;
    this.includeCancelFAQLink = true;

    // get user's profile
    const userProfile = await lastValueFrom(this.profile$.pipe(take(1)));
    // check if cancellation feature toggle is enabled 
    if (this.cancelPlanEnabled) {
      // check if customer resides in US
      if (userProfile?.country?.toUpperCase() === 'US') {
        // check if customer has cancellable plans (has cancel button)
        if (!this.checkCancelButtons(this.myPlans?.categories)) {
          // do not show cancel message if customer has cancellable plans
          this.showCancelMessage = false;
          // do not include FAQ link if no cancel message to show
          this.includeCancelFAQLink = false;
        }
        else { // customer has no cancellable plans
          // show the cancel message
          this.showCancelMessage = true;
          // do not include FAQ link
          this.includeCancelFAQLink = false;
        }
      }
    }
  }
}
